import { SerializedError } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { buildRoleAwarePrefix, isAdvisorRole } from '@/utils/api';
import { mergeViewingTagIntoArg } from '@/utils/viewingInvestorTagMerge';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';
import {
  createApi,
  FetchBaseQueryError,
  type TypedUseQueryHookResult,
} from '@reduxjs/toolkit/query/react';
import { cashFlowApi } from './cashFlowApi';
import { RecordResponse } from '../models/ServiceTypes';
import { waServicesQuery } from './baseQueries';
import {
  confirmedViewingTagSelection,
  selectQueryViewingTag,
} from './selectQueryViewingTag';
import { WARootState } from './store';

type QueryParams = Record<string, string | number | boolean>;
type QueryError = FetchBaseQueryError | SerializedError;
type TransactionsTag =
  | 'TransactionTrends'
  | 'Transactions'
  | 'BrokerageTransactions'
  | 'BankTransactionsOverview'
  | 'BankTransactionsChart'
  | 'BankTransactions'
  | 'TransactionTypeOverrides'
  | 'BankCategoryOverrides'
  | 'UnfinishedImports'
  | 'WizardEligibility'
  | 'DuplicateTransactions'
  | { type: 'BrokerageTransactions'; id: string | number }
  | { type: 'BankTransactions'; id: string | number }
  | { type: 'BankTransactionsOverview'; id: string | number }
  | { type: 'TransactionTypeOverrides'; id: string | number }
  | { type: 'UnfinishedImports'; id: string | number };

function getRolePath(state: WARootState) {
  return String(state.auth.currentRole);
}

// interface TransactionTrends {
//   categories: Category[];
//   series: Series[];
// }

interface BrokerageTransaction {
  WealthAccountTransactionId: number;
  WealthAccountId: number;
  WealthAccountName: string;
  InstitutionName: string;
  InstitutionId: number;
  CategoryName: string;
  Description: string;
  Amount: number;
  Quantity: number;
  TransactionDate: string;
  TypeManuallyChanged: boolean;
  TypeChangedByRule: boolean;
  TradeDate: string;
  SettlementDate: string;
  CUSIP?: string;
  Symbol?: string;
  PrincipalCashAmount?: number;
  IncomeCashAmount?: number;
}

interface SortParam {
  Property: string;
  Direction: 'asc' | 'desc';
  Order: number;
}

export interface GetBrokerageTransactionsQueryArg {
  startDate: string;
  endDate: string;
  sort?: SortParam[];
  limit?: number;
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
}

export interface GetBankTransactionsQueryArg {
  startDate: string;
  endDate: string;
  limit?: number;
  page?: number;
  sort?: { Property: string; Direction: 'asc' | 'desc' };
  search?: string;
  hideTransfers?: boolean;
  OnlyUncategorized?: boolean;
  ExcludedBankTransactionCategories?: number[];
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

export interface GetCashFlowWizardEligibilityQueryArg {
  startDate: string;
  endDate: string;
  hideTransfers?: boolean;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

export interface GetBankTransactionsChartQueryArg {
  startDate: string;
  endDate: string;
  hideTransfers?: boolean;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

export interface GetBankTransactionsOverviewQueryArg {
  startDate: string;
  endDate: string;
  hideTransfers?: boolean;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

export interface GetDuplicateTransactionsQueryArg {
  startDate: string;
  endDate: string;
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

export function mergeViewingInvestorTagIntoTransactionsArg(
  arg: GetBrokerageTransactionsQueryArg,
  selection: ViewingTagSelection,
): GetBrokerageTransactionsQueryArg {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function mergeViewingInvestorTagIntoBankTransactionsArg(
  arg: GetBankTransactionsQueryArg,
  selection: ViewingTagSelection,
): GetBankTransactionsQueryArg {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildBankTransactionsSubscriptionArg(
  arg: GetBankTransactionsQueryArg,
  selection: ViewingTagSelection,
): GetBankTransactionsQueryArg {
  return mergeViewingInvestorTagIntoBankTransactionsArg(arg, selection);
}

export function mergeViewingInvestorTagIntoCashFlowSupportArg<
  T extends { InvestorId?: number; InvestorTagId?: number; TagId?: number },
>(arg: T, selection: ViewingTagSelection): T {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildCashFlowWizardEligibilitySubscriptionArg(
  arg: GetCashFlowWizardEligibilityQueryArg,
  selection: ViewingTagSelection,
): GetCashFlowWizardEligibilityQueryArg {
  return mergeViewingInvestorTagIntoCashFlowSupportArg(arg, selection);
}

export function buildBankTransactionsChartSubscriptionArg(
  arg: GetBankTransactionsChartQueryArg,
  selection: ViewingTagSelection,
): GetBankTransactionsChartQueryArg {
  return mergeViewingInvestorTagIntoCashFlowSupportArg(arg, selection);
}

export function buildBankTransactionsOverviewSubscriptionArg(
  arg: GetBankTransactionsOverviewQueryArg,
  selection: ViewingTagSelection,
): GetBankTransactionsOverviewQueryArg {
  return mergeViewingInvestorTagIntoCashFlowSupportArg(arg, selection);
}

export function buildDuplicateTransactionsSubscriptionArg(
  arg: GetDuplicateTransactionsQueryArg,
  selection: ViewingTagSelection,
): GetDuplicateTransactionsQueryArg {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export interface BankTransactionsOverview {
  HasTransactions: boolean;
  NumberOfDuplicates: number;
  NumberUncatorizedInDateRange: number;
  PercentUncategorizedInDateRange: number;
  NumberOfPositiveUncategorizedInDateRange: number;
  TotalExpenseAmount: number;
  TotalIncomeAmount: number;
  TransferCategoryId: number;
  IncomeCategoryId: number;
}

export interface CashFlowChartPoint {
  id: number;
  name: string;
  y: number;
  percent: number;
}

export interface CashFlowChartSeries {
  id: string;
  name: string;
  color?: string;
  data: CashFlowChartPoint[];
}

export interface CashFlowChartResponse {
  Records: CashFlowChartSeries[];
  TotalLength: number;
}

export interface WizardEligibilityResponse {
  ShowPositiveTransactionsWizard: boolean;
  ShowUncategorizedTransactionsWizard: boolean;
  ShowDuplicateTransactionsWizard: boolean;
  HasTransactions: boolean;
  HasIncome: boolean;
  PositiveUncategorizedCount: number;
  UncategorizedCount: number;
  DuplicatesCount: number;
  IncomeCategoryId?: number;
  TransferCategoryId?: number;
  LastIncomeDate?: string;
}

export interface DuplicateTransactionResponse {
  WealthAccountId: number;
  MinWealthAccountTransactionId: number;
  AccountName: string;
  Description: string;
  Amount: number;
  TransactionDate: string;
  Count: number;
  IsDuplicate?: boolean | null;
}

export interface BankTransaction {
  WealthAccountTransactionId: string;
  WealthAccountId?: number;
  AccountUrl?: string | null;
  TransactionDate: string;
  DisplayDescription: string;
  CategoryName: string;
  BankTransactionCategoryId: number;
  SecurityDescription: string;
  Name: string;
  Symbol: string;
  CUSIP: string;
  Amount: number;
  CanEdit: boolean;
  OriginalDescription: string;
  TransactionType: string;
  NewTransactionTypeId?: number;
  AccountNumber: string;
  TradeDate: string;
  SettlementDate: string;
  ExDate: string | null;
  TypeOfActivity: string;
  CashflowGroup: string;
  AlwaysChange?: boolean;
  IsCustomCategory?: boolean;
  ParentCategoryId?: number;
  ParentCategoryName?: string;
  TypeManuallyChanged?: boolean;
  TypeChangedByRule?: boolean;
  ComparisonOperator?: number;
  ComparisonText?: string;
  MerchantLogoUrl?: string;
}

export interface AddTransactionResponse extends BankTransaction {
  WasCreated?: boolean;
  IsVisibleInCashflow?: boolean;
  HiddenReason?: string;
  ResolvedTransactionType?: string;
  AppliedTransactionTypeOverrideId?: number;
  AppliedRuleText?: string;
  AppliedComparisonOperator?: number;
  AppliedRuleWealthAccountId?: number | null;
}

type BankTransactionsResponse = RecordResponse<BankTransaction>;

export interface TransactionTrendsResponse {
  Records: {
    categories: Array<{
      value: number;
      name: string;
      id: string;
    }>;
    series: Array<{
      name: string;
      id: number;
      data: number[];
      stack: string;
      color: string;
    }>;
  };
  TotalLength: number;
}

// Define the Unfinished Import type
export interface UnfinishedImport {
  UploadId: number;
  ImportDate: string;
  NumberOfTemporaryTransactions: number;
}

// Define the response type for the unfinished imports endpoint
type UnfinishedImportsResponse = RecordResponse<UnfinishedImport>;

// --- Account Association Types ---
interface AssociateAccountPayload {
  AccountName: string;
  WealthAccountId: number;
}

interface UploadCategoryMapping {
  CategoryName?: string;
  BankTransactionCategoryId?: number;
}

interface AssociateUploadRequest {
  Accounts: AssociateAccountPayload[];
  Categories: UploadCategoryMapping[];
  UploadId: number;
  NumberOfTemporaryTransactions: number;
}

interface AssociateUploadResponse {
  NumberOfTransactionsAdded: number;
  UploadId: number; // Usually 0 on success
  NumberOfTemporaryTransactions: number; // Usually 0 on success
}
// --- End Account Association Types ---

// Define TransactionTypeOverride type based on example response
export interface TransactionTypeOverride {
  WealthAccountTransactionTypeOverrideId: number;
  DisplayDescription: string;
  TransactionTypeId: number;
  ComparisonOperator: number;
  ComparisonOperatorDisplay: string;
  TransactionTypeDisplay: string;
}

const BANK_TRANSACTION_SUMMARY_TAGS = [
  'BankTransactions',
  'BankTransactionsChart',
  'WizardEligibility',
  // Keep the duplicate wizard in lockstep with the banner: any transaction
  // add/update can grow or shrink a duplicate set, so the wizard's list must
  // refetch alongside the WizardEligibility banner count (EUD-775).
  'DuplicateTransactions',
] satisfies TransactionsTag[];

export const transactionsApi = createApi({
  reducerPath: 'transactionsApi',
  keepUnusedDataFor: 600,
  tagTypes: [
    'TransactionTrends',
    'Transactions',
    'BrokerageTransactions',
    'BankTransactionsOverview',
    'BankTransactionsChart',
    'BankTransactions',
    'TransactionTypeOverrides',
    'BankCategoryOverrides',
    'UnfinishedImports',
    'WizardEligibility',
    'DuplicateTransactions',
  ],
  baseQuery: waServicesQuery,
  endpoints: (builder) => ({
    getTransactionTrends: builder.query<
      TransactionTrendsResponse,
      {
        startDate: string;
        endDate: string;
        trendType?: string;
        hideTransfers?: boolean;
        ExcludedAccounts?: number[];
        ExcludedAccountTypes?: number[];
        ExcludedCategories?: number[];
      }
    >({
      queryFn: async (
        {
          startDate,
          endDate,
          trendType = 'combined',
          hideTransfers = true,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
        },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        const result = await baseQuery({
          url: `/api/${currentRolePath}/BankTransactionHistoricalTrends`,
          method: 'GET',
          params: {
            trendType,
            startDate,
            endDate,
            hideTransfers,
            ...(ExcludedAccounts && ExcludedAccounts.length
              ? { ExcludedAccounts: ExcludedAccounts.join(',') }
              : {}),
            ...(ExcludedAccountTypes && ExcludedAccountTypes.length
              ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
              : {}),
            ...(ExcludedCategories && ExcludedCategories.length
              ? { ExcludedCategories: ExcludedCategories.join(',') }
              : {}),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as TransactionTrendsResponse };
      },
      providesTags: ['TransactionTrends'],
    }),

    getBrokerageTransactions: builder.query<
      RecordResponse<BrokerageTransaction>,
      GetBrokerageTransactionsQueryArg
    >({
      queryFn: async (
        {
          startDate,
          endDate,
          sort,
          limit = 50000,
          InvestorId,
          InvestorTagId,
          TagId,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
        },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const params: QueryParams = {
          startDate,
          endDate,
          Limit: limit,
          ...(ExcludedAccounts?.length
            ? { ExcludedAccounts: ExcludedAccounts.join(',') }
            : {}),
          ...(ExcludedAccountTypes?.length
            ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
            : {}),
          ...(ExcludedCategories?.length
            ? { ExcludedCategories: ExcludedCategories.join(',') }
            : {}),
          ...(typeof InvestorTagId !== 'undefined' ? { InvestorTagId } : {}),
          ...(typeof TagId !== 'undefined' ? { TagId } : {}),
        };

        if (sort && sort.length > 0) {
          sort.forEach((s, index) => {
            const sortIndex = String(index);
            params[`sort[${sortIndex}].Property`] = s.Property;
            params[`sort[${sortIndex}].Direction`] = s.Direction;
            params[`sort[${sortIndex}].Order`] = s.Order;
          });
        } else {
          params['sort[0].Property'] = 'TransactionDate';
          params['sort[0].Direction'] = 'desc';
          params['sort[0].Order'] = 0;
        }

        const result = await baseQuery({
          url: `${buildRoleAwarePrefix(currentRole, InvestorId)}/BrokerageTransactions`,
          method: 'GET',
          params: params,
        });

        if (result.error) {
          console.error('Error fetching brokerage transactions:', result.error);
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<BrokerageTransaction> };
      },
      providesTags: (result) =>
        result?.Records
          ? [
              ...result.Records.map(({ WealthAccountTransactionId }) => ({
                type: 'BrokerageTransactions' as const,
                id: WealthAccountTransactionId,
              })),
              { type: 'BrokerageTransactions', id: 'LIST' },
            ]
          : [{ type: 'BrokerageTransactions', id: 'LIST' }],
    }),

    getBrokerageTransactionsByAccount: builder.query<
      RecordResponse<BrokerageTransaction>,
      {
        accountId: number;
        startDate: string;
        endDate: string;
        limit?: number;
        sort?: SortParam[];
        InvestorId?: number;
      }
    >({
      queryFn: async (
        { accountId, startDate, endDate, limit = 50000, sort, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const currentRolePath = getRolePath(state);
        const investorScoped =
          isAdvisorRole(currentRole) && typeof InvestorId === 'number';

        const params: QueryParams = {
          startDate,
          endDate,
          Limit: limit,
          ...(investorScoped ? { AccountId: accountId } : {}),
        };

        if (sort && sort.length > 0) {
          sort.forEach((s, index) => {
            const sortIndex = String(index);
            params[`sort[${sortIndex}].Property`] = s.Property;
            params[`sort[${sortIndex}].Direction`] = s.Direction;
            params[`sort[${sortIndex}].Order`] = s.Order;
          });
        } else {
          params['sort[0].Property'] = 'TransactionDate';
          params['sort[0].Direction'] = 'desc';
          params['sort[0].Order'] = 0;
        }

        const result = await baseQuery({
          url: investorScoped
            ? `${buildRoleAwarePrefix(currentRole, InvestorId)}/BrokerageTransactions`
            : `/api/${currentRolePath}/Accounts/${String(accountId)}/BrokerageTransactions`,
          method: 'GET',
          params: params,
        });

        if (result.error) {
          console.error(
            'Error fetching brokerage transactions by account:',
            result.error,
          );
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<BrokerageTransaction> };
      },
      providesTags: (result, error, { accountId }) => [
        { type: 'BrokerageTransactions', id: accountId },
      ],
      extraOptions: { maxRetries: 3 },
    }),
    getBankTransactionsOverview: builder.query<
      BankTransactionsOverview,
      GetBankTransactionsOverviewQueryArg
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const {
          startDate,
          endDate,
          hideTransfers = true,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          InvestorId,
          InvestorTagId,
          TagId,
        } = mergeViewingInvestorTagIntoCashFlowSupportArg(
          args,
          confirmedViewingTagSelection(state),
        );

        const result = await baseQuery({
          url: `${buildRoleAwarePrefix(currentRole, InvestorId)}/bankTransactionsOverview`,
          method: 'GET',
          params: {
            startDate,
            endDate,
            hideTransfers,
            ...(ExcludedAccounts && ExcludedAccounts.length
              ? { ExcludedAccounts: ExcludedAccounts.join(',') }
              : {}),
            ...(ExcludedAccountTypes && ExcludedAccountTypes.length
              ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
              : {}),
            ...(ExcludedCategories && ExcludedCategories.length
              ? { ExcludedCategories: ExcludedCategories.join(',') }
              : {}),
            ...(typeof InvestorTagId !== 'undefined' ? { InvestorTagId } : {}),
            ...(typeof TagId !== 'undefined' ? { TagId } : {}),
          },
        });

        if (result.error) {
          console.error(
            'Error fetching bank transactions overview:',
            result.error,
          );
          return { error: result.error };
        }

        return { data: result.data as BankTransactionsOverview };
      },
      providesTags: ['BankTransactionsOverview'],
    }),
    getBankTransactionsOverviewByAccount: builder.query<
      BankTransactionsOverview,
      {
        accountId: number;
        startDate: string;
        endDate: string;
        hideTransfers?: boolean;
        ExcludedAccounts?: number[];
        ExcludedAccountTypes?: number[];
        ExcludedCategories?: number[];
        InvestorId?: number;
      }
    >({
      queryFn: async (
        {
          accountId,
          startDate,
          endDate,
          hideTransfers = true,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          InvestorId,
        },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;

        const result = await baseQuery({
          url: `${buildRoleAwarePrefix(state.auth.currentRole, InvestorId)}/bankTransactionsOverview`,
          method: 'GET',
          params: {
            startDate,
            endDate,
            hideTransfers,
            WealthAccountId: accountId,
            ...(ExcludedAccounts && ExcludedAccounts.length
              ? { ExcludedAccounts: ExcludedAccounts.join(',') }
              : {}),
            ...(ExcludedAccountTypes && ExcludedAccountTypes.length
              ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
              : {}),
            ...(ExcludedCategories && ExcludedCategories.length
              ? { ExcludedCategories: ExcludedCategories.join(',') }
              : {}),
          },
        });

        if (result.error) {
          console.error(
            'Error fetching bank transactions overview by account:',
            result.error,
          );
          return { error: result.error };
        }

        return { data: result.data as BankTransactionsOverview };
      },
      providesTags: (result, error, { accountId }) => [
        { type: 'BankTransactionsOverview', id: accountId },
      ],
    }),

    getCashFlowWizardEligibility: builder.query<
      WizardEligibilityResponse,
      GetCashFlowWizardEligibilityQueryArg
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const {
          startDate,
          endDate,
          hideTransfers = true,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          InvestorId,
          InvestorTagId,
          TagId,
        } = mergeViewingInvestorTagIntoCashFlowSupportArg(
          args,
          confirmedViewingTagSelection(state),
        );

        const result = await baseQuery({
          url: `${buildRoleAwarePrefix(currentRole, InvestorId)}/cashflow/wizard-eligibility`,
          method: 'GET',
          params: {
            startDate,
            endDate,
            hideTransfers,
            ...(ExcludedAccounts && ExcludedAccounts.length
              ? { ExcludedAccounts: ExcludedAccounts.join(',') }
              : {}),
            ...(ExcludedAccountTypes && ExcludedAccountTypes.length
              ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
              : {}),
            ...(ExcludedCategories && ExcludedCategories.length
              ? { ExcludedCategories: ExcludedCategories.join(',') }
              : {}),
            ...(typeof InvestorTagId !== 'undefined' ? { InvestorTagId } : {}),
            ...(typeof TagId !== 'undefined' ? { TagId } : {}),
          },
        });

        if (result.error) {
          console.error('Error fetching wizard eligibility:', result.error);
          return { error: result.error };
        }

        return { data: result.data as WizardEligibilityResponse };
      },
      providesTags: ['WizardEligibility'],
    }),

    getBankTransactionsChart: builder.query<
      CashFlowChartResponse,
      GetBankTransactionsChartQueryArg
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const {
          startDate,
          endDate,
          hideTransfers = true,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          InvestorId,
          InvestorTagId,
          TagId,
        } = mergeViewingInvestorTagIntoCashFlowSupportArg(
          args,
          confirmedViewingTagSelection(state),
        );

        const result = await baseQuery({
          url: `${buildRoleAwarePrefix(currentRole, InvestorId)}/BankTransactionsChart`,
          method: 'GET',
          params: {
            startDate,
            endDate,
            hideTransfers,
            ...(ExcludedAccounts && ExcludedAccounts.length
              ? { ExcludedAccounts: ExcludedAccounts.join(',') }
              : {}),
            ...(ExcludedAccountTypes && ExcludedAccountTypes.length
              ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
              : {}),
            ...(ExcludedCategories && ExcludedCategories.length
              ? { ExcludedCategories: ExcludedCategories.join(',') }
              : {}),
            ...(typeof InvestorTagId !== 'undefined' ? { InvestorTagId } : {}),
            ...(typeof TagId !== 'undefined' ? { TagId } : {}),
          },
        });

        if (result.error) {
          console.error(
            'Error fetching bank transactions chart:',
            result.error,
          );
          return { error: result.error };
        }

        return { data: result.data as CashFlowChartResponse };
      },
      providesTags: ['BankTransactionsChart'],
    }),

    getDuplicateTransactions: builder.query<
      RecordResponse<DuplicateTransactionResponse>,
      GetDuplicateTransactionsQueryArg
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const { startDate, endDate, InvestorId, InvestorTagId, TagId } =
          buildDuplicateTransactionsSubscriptionArg(
            args,
            confirmedViewingTagSelection(state),
          );
        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);

        const result = await baseQuery({
          url: `${prefix}/BankTransactionDuplicates`,
          method: 'GET',
          params: {
            startDate,
            endDate,
            ...(typeof InvestorTagId !== 'undefined' ? { InvestorTagId } : {}),
            ...(typeof TagId !== 'undefined' ? { TagId } : {}),
          },
        });

        if (result.error) {
          console.error('Error fetching duplicate transactions:', result.error);
          return { error: result.error };
        }

        return {
          data: result.data as RecordResponse<DuplicateTransactionResponse>,
        };
      },
      providesTags: ['DuplicateTransactions'],
    }),

    // Transactions endpoint
    getBankTransactions: builder.query<
      { transactions: BankTransaction[]; totalLength: number },
      GetBankTransactionsQueryArg
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const {
          startDate,
          endDate,
          limit = 500,
          page = 1,
          sort = { Property: 'TransactionDate', Direction: 'desc' },
          search,
          hideTransfers,
          OnlyUncategorized,
          ExcludedBankTransactionCategories,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          InvestorId,
          InvestorTagId,
          TagId,
        } = mergeViewingInvestorTagIntoBankTransactionsArg(
          args,
          confirmedViewingTagSelection(state),
        );

        // Build params object
        const params: QueryParams = {
          startDate,
          endDate,
          limit,
          page,
        };

        // Add optional params if they exist
        params['sort[0].Property'] = sort.Property;
        params['sort[0].Direction'] = sort.Direction;
        params['sort[0].Order'] = 0;

        if (search) {
          params.search = search;
        }

        if (hideTransfers !== undefined) {
          params.hideTransfers = hideTransfers;
        }

        if (OnlyUncategorized !== undefined) {
          params.OnlyUncategorized = OnlyUncategorized;
        }

        if (ExcludedBankTransactionCategories?.length) {
          params.ExcludedBankTransactionCategories =
            ExcludedBankTransactionCategories.join(',');
        }

        if (ExcludedAccounts?.length) {
          params.ExcludedAccounts = ExcludedAccounts.join(',');
        }

        if (ExcludedAccountTypes?.length) {
          params.ExcludedAccountTypes = ExcludedAccountTypes.join(',');
        }

        if (ExcludedCategories?.length) {
          params.ExcludedCategories = ExcludedCategories.join(',');
        }
        if (typeof InvestorTagId !== 'undefined') {
          params.InvestorTagId = InvestorTagId;
        }
        if (typeof TagId !== 'undefined') {
          params.TagId = TagId;
        }

        try {
          const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/BankTransactions`;
          const result = await baseQuery({
            url,
            method: 'GET',
            params,
          });

          if (result.error) {
            console.error('BANK TRANSACTIONS API ERROR', result.error);
            return { error: result.error };
          }

          if (!result.data) {
            console.error('BANK TRANSACTIONS API RETURNED NO DATA');
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'No data returned from API' },
                error: 'No data returned from API',
              } as FetchBaseQueryError,
            };
          }

          const data = result.data as BankTransactionsResponse;
          return {
            data: {
              transactions: data.Records,
              totalLength: data.TotalLength,
            },
          };
        } catch (err) {
          console.error('BANK TRANSACTIONS API EXCEPTION:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ['BankTransactions'],
      extraOptions: { maxRetries: 3 },
    }),
    getBankTransactionsByAccount: builder.query<
      { transactions: BankTransaction[]; totalLength: number },
      {
        accountId: number;
        startDate: string;
        endDate: string;
        limit?: number;
        page?: number;
        sort?: SortParam[];
        ExcludedBankTransactionCategories?: string[];
        ExcludedAccounts?: number[];
        ExcludedAccountTypes?: number[];
        ExcludedCategories?: number[];
        InvestorId?: number;
      }
    >({
      queryFn: async (
        {
          accountId,
          startDate,
          endDate,
          limit,
          page,
          sort,
          ExcludedBankTransactionCategories,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          InvestorId,
        },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;

        const params: QueryParams = {
          WealthAccountId: accountId,
          StartDate: startDate,
          EndDate: endDate,
        };

        if (limit) {
          params.Limit = limit;
        }

        if (page) {
          params.Page = page;
        }

        if (sort?.length) {
          params.Sort = JSON.stringify(sort);
        }

        if (ExcludedBankTransactionCategories?.length) {
          params.ExcludedBankTransactionCategories =
            ExcludedBankTransactionCategories.join(',');
        }

        if (ExcludedAccounts?.length) {
          params.ExcludedAccounts = ExcludedAccounts.join(',');
        }

        if (ExcludedAccountTypes?.length) {
          params.ExcludedAccountTypes = ExcludedAccountTypes.join(',');
        }

        if (ExcludedCategories?.length) {
          params.ExcludedCategories = ExcludedCategories.join(',');
        }

        try {
          const result = await baseQuery({
            url: `${buildRoleAwarePrefix(state.auth.currentRole, InvestorId)}/BankTransactions`,
            method: 'GET',
            params,
          });

          if (result.error) {
            console.error(
              'BANK TRANSACTIONS BY ACCOUNT API ERROR',
              result.error,
            );
            return { error: result.error };
          }

          if (!result.data) {
            console.error('BANK TRANSACTIONS BY ACCOUNT API RETURNED NO DATA');
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'No data returned from API' },
                error: 'No data returned from API',
              } as FetchBaseQueryError,
            };
          }

          const data = result.data as BankTransactionsResponse;
          return {
            data: {
              transactions: data.Records,
              totalLength: data.TotalLength,
            },
          };
        } catch (err) {
          console.error('BANK TRANSACTIONS BY ACCOUNT API EXCEPTION:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (result, error, { accountId }) => [
        { type: 'BankTransactions', id: accountId },
        'BankTransactions',
        { type: 'BankTransactions', id: 'LIST' },
      ],
      extraOptions: { maxRetries: 3 },
    }),
    // Update transaction
    updateTransaction: builder.mutation<
      undefined,
      { transactionId: string; transaction: Partial<BankTransaction> }
    >({
      queryFn: async (
        { transactionId, transaction },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/BankTransactions/${transactionId}`,
            method: 'PUT',
            body: transaction,
          });

          if (result.error) {
            return { error: result.error };
          }

          return { data: undefined };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Cross-slice invalidation: ensure overrides refetch after a successful bank transaction update
          dispatch(cashFlowApi.util.invalidateTags(['BankCategoryOverrides']));
        } catch {
          // no-op on failure
        }
      },
      invalidatesTags: (result, error, arg) => {
        const tags: TransactionsTag[] = [...BANK_TRANSACTION_SUMMARY_TAGS];
        // If a rule comparator was changed, keep overrides fresh
        if (arg.transaction.ComparisonOperator !== undefined) {
          tags.push('BankCategoryOverrides');
        }
        // If transaction type changed (e.g., move to Investment Activity), refresh brokerage grids
        if (arg.transaction.NewTransactionTypeId !== undefined) {
          tags.push('BrokerageTransactions');
        }
        return tags;
      },
    }),

    // Update transaction
    updateBrokerageTransaction: builder.mutation<
      undefined,
      { transactionId: string; transaction: Partial<BankTransaction> }
    >({
      queryFn: async (
        { transactionId, transaction },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/BrokerageTransactions/${transactionId}`,
            method: 'PUT',
            body: transaction,
          });

          if (result.error) {
            return { error: result.error };
          }

          return { data: undefined };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (result, error, arg) => {
        const tags: TransactionsTag[] = ['BrokerageTransactions'];
        // Keep overrides fresh if comparator changed on a brokerage tx rule
        if (arg.transaction.ComparisonOperator !== undefined) {
          tags.push('BankCategoryOverrides');
        }
        // If transaction type changed (e.g., moved to Cash Flow), refresh bank transactions grids
        if (arg.transaction.NewTransactionTypeId !== undefined) {
          tags.push('BankTransactions');
          tags.push('BankTransactionsChart');
          const wealthAccountId = arg.transaction.WealthAccountId;
          if (wealthAccountId != null) {
            tags.push({ type: 'BankTransactions', id: wealthAccountId });
          }
        }
        return tags;
      },
    }),

    // Bulk update transactions
    bulkUpdateTransactions: builder.mutation<
      undefined,
      { transactions: Partial<BankTransaction>[] }
    >({
      queryFn: async ({ transactions }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/BankTransactions/bulk`,
            method: 'PUT',
            body: transactions,
          });

          if (result.error) {
            return { error: result.error };
          }

          return { data: undefined };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: [...BANK_TRANSACTION_SUMMARY_TAGS],
    }),

    resolveDuplicateTransactionSet: builder.mutation<
      boolean,
      {
        duplicateTransaction: DuplicateTransactionResponse;
        InvestorId?: number;
      }
    >({
      queryFn: async (
        { duplicateTransaction, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);

        try {
          const result = await baseQuery({
            url: `${prefix}/BankTransactionDuplicates/${String(duplicateTransaction.MinWealthAccountTransactionId)}`,
            method: 'PUT',
            body: duplicateTransaction,
          });

          if (result.error) {
            return { error: result.error };
          }

          return { data: Boolean(result.data) };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: [
        'BankTransactions',
        'BankTransactionsChart',
        'WizardEligibility',
      ],
    }),

    // Add new transaction
    addTransaction: builder.mutation<
      AddTransactionResponse,
      {
        DisplayDescription: string;
        BankTransactionCategoryId: number;
        WealthAccountId: number;
        Amount: number;
        TransactionDate: string;
      }
    >({
      queryFn: async (transactionInput, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/BankTransactions`,
            method: 'POST',
            body: transactionInput,
          });

          if (result.error) {
            console.error('Error adding transaction:', result.error);
            return { error: result.error };
          }

          if (!result.data) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: {
                  message: 'addTransactionModal.confirmLocationWarning',
                },
                error: 'addTransactionModal.confirmLocationWarning',
              } as FetchBaseQueryError,
            };
          }

          return { data: result.data as AddTransactionResponse };
        } catch (err) {
          console.error('Exception adding transaction:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (result, _error, arg) => {
        if (result?.IsVisibleInCashflow === false) {
          return [
            // Ensure InvestmentActivity (brokerage) refreshes when rules move items
            'BrokerageTransactions',
            { type: 'BrokerageTransactions', id: arg.WealthAccountId },
            // Match legacy UI's follow-up fetch of overrides
            'BankCategoryOverrides',
          ];
        }

        return [
          ...BANK_TRANSACTION_SUMMARY_TAGS,
          { type: 'BankTransactions', id: arg.WealthAccountId },
          'BankTransactionsOverview',
          'TransactionTrends',
          // Ensure InvestmentActivity (brokerage) refreshes when rules move items
          'BrokerageTransactions',
          { type: 'BrokerageTransactions', id: arg.WealthAccountId },
          // Match legacy UI's follow-up fetch of overrides
          'BankCategoryOverrides',
        ];
      },
    }),

    // Upload transactions from CSV
    uploadTransactions: builder.mutation<
      UploadTransactionsResponse, // Define this response type
      { file: File }
    >({
      queryFn: async ({ file }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        const formData = new FormData();
        // The API expects 'filename' and 'file' fields in the form data
        formData.append('filename', file.name);
        formData.append('file', file, file.name);

        try {
          // We need to use baseQuery directly and bypass default content-type handling
          // by providing FormData. Fetch API correctly sets multipart/form-data header.
          const result = await baseQuery({
            url: `/api/${currentRolePath}/transactions/uploads/`, // Note the trailing slash based on cURL
            method: 'POST',
            body: formData,
            // Let the browser set the correct Content-Type header for FormData
            // headers: { 'Content-Type': undefined } // Usually not needed, Fetch handles FormData correctly
          });

          if (result.error) {
            console.error('Error uploading transactions:', result.error);
            return { error: result.error };
          }

          // TODO: Need to confirm the actual success response structure from the API
          // Assuming it matches the example provided
          return { data: result.data as UploadTransactionsResponse };
        } catch (err) {
          console.error('Exception uploading transactions:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      // Decide on invalidation based on what the upload affects
      // It likely affects BankTransactions, Overview, and Trends
      invalidatesTags: [
        ...BANK_TRANSACTION_SUMMARY_TAGS,
        'BankTransactionsOverview',
        'TransactionTrends',
      ],
    }),

    // Get unfinished imports
    getUnfinishedImports: builder.query<UnfinishedImportsResponse, undefined>({
      queryFn: async (_, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/transactions/uploads/`, // GET endpoint for unfinished imports
            method: 'GET',
          });

          if (result.error) {
            console.error('Error fetching unfinished imports:', result.error);
            return { error: result.error };
          }

          return { data: result.data as UnfinishedImportsResponse };
        } catch (err) {
          console.error('Exception fetching unfinished imports:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (result) =>
        result?.Records
          ? [
              ...result.Records.map(({ UploadId }) => ({
                type: 'UnfinishedImports' as const,
                id: UploadId,
              })),
              { type: 'UnfinishedImports', id: 'LIST' },
            ]
          : [{ type: 'UnfinishedImports', id: 'LIST' }],
    }),

    // Associate uploaded transactions with an account
    associateUploadAccount: builder.mutation<
      AssociateUploadResponse,
      AssociateUploadRequest
    >({
      queryFn: async (associationData, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/transactions/uploads/${String(
              associationData.UploadId,
            )}`,
            method: 'PUT',
            body: associationData,
          });

          if (result.error) {
            console.error('Error associating upload account:', result.error);
            return { error: result.error };
          }

          return { data: result.data as AssociateUploadResponse };
        } catch (err) {
          console.error('Exception associating upload account:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      // Invalidate relevant data after successful association. Reuse the shared
      // summary tags (which include WizardEligibility + DuplicateTransactions) so
      // the Cash Flow banner and duplicate wizard refresh with the same counts as
      // a direct upload, instead of keeping pre-upload values until an unrelated
      // refetch (EUD-414 keeps the modal mounted, so the user sees this).
      invalidatesTags: [
        ...BANK_TRANSACTION_SUMMARY_TAGS,
        'BankTransactionsOverview',
        'TransactionTrends',
        'UnfinishedImports',
        // Also refresh brokerage listings since categorized items may appear there
        'BrokerageTransactions',
        // Keep overrides fresh to mirror legacy behavior
        'BankCategoryOverrides',
      ],
    }),

    // Transaction Type Overrides endpoint
    getTransactionTypeOverrides: builder.query<
      RecordResponse<TransactionTypeOverride>,
      { sort?: SortParam[]; InvestorId?: number } | undefined
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const sort = args?.sort;

        // Construct the query parameters
        const params = new URLSearchParams();
        if (sort && sort.length > 0) {
          sort.forEach((s, index) => {
            const sortIndex = String(index);
            params.set(`sort[${sortIndex}].Property`, s.Property);
            params.set(`sort[${sortIndex}].Direction`, s.Direction);
            params.set(`sort[${sortIndex}].Order`, String(s.Order));
          });
        } else {
          // Default sort
          params.set('sort[0].Property', 'DisplayDescription');
          params.set('sort[0].Direction', 'asc');
          params.set('sort[0].Order', '0');
        }

        const prefix = buildRoleAwarePrefix(currentRole, args?.InvestorId);

        const result = await baseQuery({
          url: `${prefix}/transactionTypeOverrides?${params.toString()}`,
          method: 'GET',
        });

        if (result.error) {
          console.error(
            'Error fetching transaction type overrides:',
            result.error,
          );
          return { error: result.error };
        }

        // Assume the response shape matches RecordResponse<TransactionTypeOverride>
        const data = result.data as RecordResponse<TransactionTypeOverride>;
        return { data };
      },
      providesTags: (result) =>
        result?.Records
          ? [
              ...result.Records.map(
                ({ WealthAccountTransactionTypeOverrideId }) => ({
                  type: 'TransactionTypeOverrides' as const,
                  id: WealthAccountTransactionTypeOverrideId,
                }),
              ),
              { type: 'TransactionTypeOverrides', id: 'LIST' },
            ]
          : [{ type: 'TransactionTypeOverrides', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    // Create Transaction Type Override (POST)
    createTransactionTypeOverride: builder.mutation<
      TransactionTypeOverride,
      {
        payload: {
          ComparisonOperator: number;
          DisplayDescription: string;
          TransactionTypeId: number;
          WealthAccountId?: number | null;
        };
        InvestorId?: number;
      }
    >({
      queryFn: async ({ payload, InvestorId }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);

        const result = await baseQuery({
          url: `${prefix}/transactionTypeOverrides`,
          method: 'POST',
          body: payload,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as TransactionTypeOverride };
      },
      invalidatesTags: [
        { type: 'TransactionTypeOverrides', id: 'LIST' },
        'BankTransactions',
        'BankTransactionsChart',
        'BrokerageTransactions',
        'BankTransactionsOverview',
        'TransactionTrends',
        'BankCategoryOverrides',
      ],
      extraOptions: { maxRetries: 1 },
    }),

    // Delete transaction
    deleteTransaction: builder.mutation<undefined, string>({
      queryFn: async (transactionId, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRolePath = getRolePath(state);

        try {
          const result = await baseQuery({
            url: `/api/${currentRolePath}/BankTransactions/${transactionId}`,
            method: 'DELETE',
          });

          if (result.error) {
            console.error(
              `Error deleting transaction ${transactionId}:`,
              result.error,
            );
            return { error: result.error };
          }

          // Check if the result data is explicitly false, indicating a failed delete
          if (result.data === false) {
            const errorMsg = `Server indicated failed deletion for transaction ${transactionId}`;
            console.error(errorMsg);
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: errorMsg },
                error: errorMsg,
              } as FetchBaseQueryError,
            };
          }

          // Assume true or other non-error response means success
          return { data: undefined }; // No data expected on successful delete
        } catch (err) {
          console.error(
            `Exception deleting transaction ${transactionId}:`,
            err,
          );
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: [
        ...BANK_TRANSACTION_SUMMARY_TAGS,
        'BankTransactionsOverview',
        'TransactionTrends',
      ],
    }),

    // Update Transaction Type Override endpoint
    updateTransactionTypeOverride: builder.mutation<
      TransactionTypeOverride, // Return the updated override on success
      {
        overrideId: number;
        overrideData: Partial<TransactionTypeOverride>;
        InvestorId?: number;
      }
    >({
      queryFn: async (
        { overrideId, overrideData, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);
        // The API expects the full object for PUT, so merge partial data with existing if needed,
        // or ensure the calling component fetches the full object before calling.
        // For now, assuming overrideData contains enough, or the API accepts partial.
        // Based on the cURL example, it seems the full object is sent.
        const result = await baseQuery({
          url: `${prefix}/transactionTypeOverrides/${String(overrideId)}`,
          method: 'PUT',
          body: overrideData, // Sending the potentially partial data
        });

        if (result.error) {
          console.error(
            'Error updating transaction type override:',
            result.error,
          );
          return { error: result.error };
        }

        // API returns the updated object
        return { data: result.data as TransactionTypeOverride };
      },
      invalidatesTags: (result, error, { overrideId }) =>
        result
          ? [
              { type: 'TransactionTypeOverrides', id: overrideId },
              // Match legacy affectedStores: bank + brokerage
              'BankTransactions',
              'BankTransactionsChart',
              'BrokerageTransactions',
            ]
          : [],
      extraOptions: { maxRetries: 1 },
    }),

    // Delete Transaction Type Override endpoint
    deleteTransactionTypeOverride: builder.mutation<
      boolean,
      { overrideId: number; InvestorId?: number }
    >({
      // Returns boolean based on cURL
      queryFn: async (
        { overrideId, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);

        const result = await baseQuery({
          url: `${prefix}/transactionTypeOverrides/${String(overrideId)}`,
          method: 'DELETE',
        });

        if (result.error) {
          console.error(
            'Error deleting transaction type override:',
            result.error,
          );
          // Return false on error, mimicking API's potential false return
          return { data: false }; // Or return error: result.error
        }

        // API returns true on success, false on failure
        if (result.data === false) {
          console.warn(
            `API returned false for deleting transaction type override ${String(
              overrideId,
            )}`,
          );
          return { data: false };
        }

        return { data: true }; // Assume success if no error and not explicitly false
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'TransactionTypeOverrides', id: 'LIST' },
              // Also refresh bank and brokerage transactions like legacy
              'BankTransactions',
              'BankTransactionsChart',
              'BrokerageTransactions',
            ]
          : [],
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

export const {
  useGetTransactionTrendsQuery,
  useGetBrokerageTransactionsQuery: useGetBrokerageTransactionsQueryBase,
  useGetBrokerageTransactionsByAccountQuery,
  useGetCashFlowWizardEligibilityQuery:
    useGetCashFlowWizardEligibilityQueryBase,
  useGetBankTransactionsChartQuery: useGetBankTransactionsChartQueryBase,
  useGetDuplicateTransactionsQuery: useGetDuplicateTransactionsQueryBase,
  useGetBankTransactionsOverviewByAccountQuery,
  useGetBankTransactionsQuery: useGetBankTransactionsQueryBase,
  useGetBankTransactionsByAccountQuery,
  useUpdateTransactionMutation,
  useBulkUpdateTransactionsMutation,
  useResolveDuplicateTransactionSetMutation,
  useAddTransactionMutation,
  useGetTransactionTypeOverridesQuery,
  useDeleteTransactionMutation,
  useUploadTransactionsMutation,
  useGetUnfinishedImportsQuery,
  useAssociateUploadAccountMutation,
  useUpdateTransactionTypeOverrideMutation,
  useDeleteTransactionTypeOverrideMutation,
  useUpdateBrokerageTransactionMutation,
  useCreateTransactionTypeOverrideMutation,
} = transactionsApi;

type GetBrokerageTransactionsUseQueryOptions = Parameters<
  typeof transactionsApi.endpoints.getBrokerageTransactions.useQuery
>[1];

type GetBankTransactionsUseQueryOptions = Parameters<
  typeof transactionsApi.endpoints.getBankTransactions.useQuery
>[1];

export interface GetBankTransactionsQueryResult {
  data?: { transactions: BankTransaction[]; totalLength: number };
  // Like `data`, but cleared while a request for a NEW arg is in flight (RTK
  // Query). Lets callers tell an arg change apart from a same-arg refetch.
  currentData?: { transactions: BankTransaction[]; totalLength: number };
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: QueryError;
}

type GetCashFlowWizardEligibilityUseQueryOptions = Parameters<
  typeof transactionsApi.endpoints.getCashFlowWizardEligibility.useQuery
>[1];

type GetBankTransactionsChartUseQueryOptions = Parameters<
  typeof transactionsApi.endpoints.getBankTransactionsChart.useQuery
>[1];

type GetBankTransactionsOverviewUseQueryOptions = Parameters<
  typeof transactionsApi.endpoints.getBankTransactionsOverview.useQuery
>[1];

export interface GetBankTransactionsChartQueryResult {
  data?: CashFlowChartResponse;
  // Cleared while a request for a NEW arg is in flight (see above).
  currentData?: CashFlowChartResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: QueryError;
}

export interface GetBankTransactionsOverviewQueryResult {
  data?: BankTransactionsOverview;
  // Cleared while a request for a NEW arg is in flight (see above).
  currentData?: BankTransactionsOverview;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: QueryError;
}

type GetDuplicateTransactionsUseQueryOptions = Parameters<
  typeof transactionsApi.endpoints.getDuplicateTransactions.useQuery
>[1];

// Forwarding `options` through the wrapper collapses the overloaded `useQuery`
// return type to `any`, so pin the result type explicitly.
type GetDuplicateTransactionsQueryResult = TypedUseQueryHookResult<
  RecordResponse<DuplicateTransactionResponse>,
  GetDuplicateTransactionsQueryArg,
  typeof waServicesQuery
>;

export function useGetBrokerageTransactionsQuery(
  arg: GetBrokerageTransactionsQueryArg | typeof skipToken,
  options?: GetBrokerageTransactionsUseQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : mergeViewingInvestorTagIntoTransactionsArg(arg, confirmed.selection);

  return transactionsApi.endpoints.getBrokerageTransactions.useQuery(
    queryArg,
    options,
  );
}

export function useGetBankTransactionsQuery(
  arg: GetBankTransactionsQueryArg | typeof skipToken,
  options?: GetBankTransactionsUseQueryOptions,
): GetBankTransactionsQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildBankTransactionsSubscriptionArg(arg, confirmed.selection);

  return transactionsApi.endpoints.getBankTransactions.useQuery(
    queryArg,
    options,
  );
}

export function useGetCashFlowWizardEligibilityQuery(
  arg: GetCashFlowWizardEligibilityQueryArg | typeof skipToken,
  options?: GetCashFlowWizardEligibilityUseQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildCashFlowWizardEligibilitySubscriptionArg(arg, confirmed.selection);

  return transactionsApi.endpoints.getCashFlowWizardEligibility.useQuery(
    queryArg,
    options,
  );
}

export function useGetBankTransactionsChartQuery(
  arg: GetBankTransactionsChartQueryArg | typeof skipToken,
  options?: GetBankTransactionsChartUseQueryOptions,
): GetBankTransactionsChartQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildBankTransactionsChartSubscriptionArg(arg, confirmed.selection);

  return transactionsApi.endpoints.getBankTransactionsChart.useQuery(
    queryArg,
    options,
  );
}

export function useGetBankTransactionsOverviewQuery(
  arg: GetBankTransactionsOverviewQueryArg | typeof skipToken,
  options?: GetBankTransactionsOverviewUseQueryOptions,
): GetBankTransactionsOverviewQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildBankTransactionsOverviewSubscriptionArg(arg, confirmed.selection);

  return transactionsApi.endpoints.getBankTransactionsOverview.useQuery(
    queryArg,
    options,
  );
}

export function useGetDuplicateTransactionsQuery(
  arg: GetDuplicateTransactionsQueryArg | typeof skipToken,
  options?: GetDuplicateTransactionsUseQueryOptions,
): GetDuplicateTransactionsQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildDuplicateTransactionsSubscriptionArg(arg, confirmed.selection);

  return transactionsApi.endpoints.getDuplicateTransactions.useQuery(
    queryArg,
    options,
  ) as GetDuplicateTransactionsQueryResult;
}

// Define the response type for the upload endpoint based on the example
interface UploadTransactionsResponse {
  NumberOfTransactionsAdded: number;
  Accounts: Array<{ AccountName: string }>; // Assuming AccountName is the key field
  Categories: UploadCategoryMapping[];
  UploadId: number;
  NumberOfTemporaryTransactions: number;
}
